import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import { WebUsageAggregates, WebUsageJob } from '../../types';
import { formatDateTimeUTC } from '../../utils/dateTime';
import ConvertedTokensChart from './ConvertedTokensChart';
import UsageBreakdownChart from './UsageBreakdownChart';
import DateRangeFilter from '../filters/DateRangeFilter';
import { EmptyRow, SectionFilterBar, SortableTh, TableCard, TableHeader, TableViewport, tdClass, usePageSize } from '../table/TableKit';
import { StatTile } from '../cards/StatCards';
import { ErrorNotice } from '../ErrorNotice';
import RefreshButton from '../RefreshButton';
import { CliCost, tokensForOperation } from './cliCost';
import { convertedTokensByDay, KindUsageRow, TokenKind } from './usageBreakdown';
import { usePageClamp } from '../../hooks/usePageClamp';
import { useUsageRange } from '../../hooks/useUsageRange';
import { urlEnum, urlIsoDate, urlPositiveInt, useUrlState } from '../../hooks/useUrlState';
import { fromHere } from '../../hooks/useBackTo';

// Fixed height so every control in the filter bar lines up.
const filterClass =
    'h-10 bg-body-bg border border-gray-600 text-body-color focus:border-primary focus:outline-none rounded-md pl-3 pr-8 py-2 select-chevron';

const emptyAggregates: WebUsageAggregates = {
    unique_count: 0,
    duplicate_count: 0,
    input_tokens_sum: 0,
    input_tokens_converted_sum: 0,
    input_tokens_supported_sum: 0,
    output_tokens_sum: 0,
    cost_cents: null,
    estimated_cost_cents: null,
    total_jobs: 0,
};


// Users never see output tokens.
const CHART_TOKEN_KINDS: TokenKind[] = ['converted', 'supported', 'input'];

const LIST_URL_SPEC = {
    operation: urlEnum(['', 'analyze', 'convert'] as const, ''),
    range: urlEnum(['7d', '30d', 'custom'] as const, '30d'),
    from: urlIsoDate(),
    to: urlIsoDate(),
    sort: urlEnum(['project_name', 'operation', 'input_tokens', 'input_tokens_converted', 'input_tokens_supported', 'cost', 'completed_at'] as const, 'completed_at'),
    order: urlEnum(['asc', 'desc'] as const, 'desc'),
    page: urlPositiveInt(),
};

function WebUsageTab({ urlPrefix }: { urlPrefix?: string }) {
    const navigate = useNavigate();
    const location = useLocation();
    const [pageSize, setPageSize] = usePageSize('web-usage');
    const [state, setUrlState] = useUrlState(LIST_URL_SPEC, urlPrefix);
    const offset = (state.page - 1) * pageSize;
    const { rangeFilter, range } = useUsageRange(state.range, state.from, state.to);

    const params = useMemo(
        () => ({
            operation: state.operation || undefined,
            from: range?.from,
            to: range?.to,
            limit: pageSize,
            offset,
            sort: state.sort,
            order: state.order,
        }),
        [state.operation, range, pageSize, offset, state.sort, state.order],
    );

    const { data: fetched, isFetching, isLoading, isPlaceholderData, error, refetch } = useQuery({
        queryKey: ['web', 'usage', params],
        queryFn: async () => (await apiService.webGetUsage(params)).data,
        placeholderData: (prev) => prev,
        retry: false,
        enabled: range !== null,
        refetchOnWindowFocus: true,
    });
    // An incomplete custom range fetches nothing. Drop the old numbers: kept on
    // screen they would read as this range's.
    const data = range ? fetched : undefined;

    const dailyParams = useMemo(
        () => ({
            operation: state.operation || undefined,
            from: range?.from,
            to: range?.to,
        }),
        [state.operation, range],
    );

    const { data: daily, error: dailyError, isFetching: isDailyFetching, refetch: refetchDaily } = useQuery({
        queryKey: ['web', 'usageDaily', dailyParams],
        queryFn: async () => (await apiService.webGetUsageDaily(dailyParams)).data,
        retry: false,
        enabled: range !== null,
        refetchOnWindowFocus: true,
    });

    const convertedRows = useMemo(() => range ? convertedTokensByDay(daily?.days ?? [], range) : [], [daily?.days, range]);

    // One series per operation.
    const chartRows: KindUsageRow[] = useMemo(
        () =>
            (daily?.days ?? []).map((day) => ({
                date: day.date,
                keyId: day.operation === 'analyze' ? 1 : 2,
                keyLabel: day.operation,
                tokensByKind: { input: day.input_tokens, converted: day.input_tokens_converted, supported: day.input_tokens_supported, output: 0 },
                count: day.jobs,
            })),
        [daily],
    );

    const jobs: WebUsageJob[] = data?.jobs ?? [];
    const aggregates = data?.aggregates ?? emptyAggregates;
    const total = data?.total ?? 0;
    // A background refetch keeps the current rows at full opacity.
    const rowsPending = isLoading || (isFetching && isPlaceholderData);

    usePageClamp({
        page: state.page,
        pageSize,
        total: data?.total,
        ready: !isLoading && !isPlaceholderData && !!data,
        setPage: (page) => setUrlState({ page }),
    });

    const onSort = (sort: string, order: 'asc' | 'desc') =>
        setUrlState({ sort: sort as typeof state.sort, order, page: 1 });

    const tiles = [
        { label: 'Jobs', value: aggregates.total_jobs.toLocaleString() },
        { label: 'Unique Entries', value: aggregates.unique_count.toLocaleString() },
        { label: 'Duplicate Entries', value: aggregates.duplicate_count.toLocaleString() },
        { label: 'In Tokens', value: aggregates.input_tokens_sum.toLocaleString() },
        { label: 'In Tokens (Converted)', value: aggregates.input_tokens_converted_sum.toLocaleString() },
        { label: 'In Tokens (Supported)', value: aggregates.input_tokens_supported_sum.toLocaleString() },
    ];

    return (
        <div>
            {error && <ErrorNotice message="Failed to load WEB usage." />}
            {/* Tab-wide filters: they scope the chart, the tiles, and the table. */}
            <SectionFilterBar>
                <select
                    aria-label="Operation filter"
                    className={filterClass}
                    value={state.operation}
                    onChange={(e) => setUrlState({ operation: e.target.value as typeof state.operation, page: 1 })}
                >
                    <option value="">All Operations</option>
                    <option value="analyze">analyze</option>
                    <option value="convert">convert</option>
                </select>
                <DateRangeFilter
                    value={rangeFilter}
                    onChange={(next) => setUrlState({ range: next.preset, from: next.from, to: next.to, page: 1 })}
                    className="!h-10"
                />
                <RefreshButton
                    onClick={() => {
                        refetch();
                        refetchDaily();
                    }}
                    fetching={isFetching || isDailyFetching}
                    disabled={!range}
                />
            </SectionFilterBar>
            <div role="group" aria-label="Usage totals" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
                {tiles.map((tile) => (
                    <StatTile key={tile.label} label={tile.label} value={tile.value} />
                ))}
            </div>
            <UsageBreakdownChart rows={chartRows} range={range} countLabel="Jobs" tokenKinds={CHART_TOKEN_KINDS} error={!!dailyError} urlPrefix={urlPrefix} />
            <ConvertedTokensChart rows={convertedRows} range={range} error={!!dailyError} />

            <TableCard>
                <TableHeader
                    title="Completed Jobs"
                    pagination={{
                        offset,
                        limit: pageSize,
                        total,
                        onOffsetChange: (nextOffset) => setUrlState({ page: nextOffset / pageSize + 1 }),
                        onLimitChange: (size) => {
                            setPageSize(size);
                            setUrlState({ page: 1 });
                        },
                    }}
                />
                <TableViewport className={rowsPending ? 'opacity-50 transition-opacity' : ''}>
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-700">
                                <SortableTh label="Project" sortKey="project_name" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                                <SortableTh label="Operation" sortKey="operation" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                                <SortableTh label="In Tokens" sortKey="input_tokens" sort={state.sort} order={state.order} onSort={onSort} />
                                <SortableTh label="In Tokens (Converted)" sortKey="input_tokens_converted" sort={state.sort} order={state.order} onSort={onSort} />
                                <SortableTh label="In Tokens (Supported)" sortKey="input_tokens_supported" sort={state.sort} order={state.order} onSort={onSort} />
                                <SortableTh label="Cost" sortKey="cost" sort={state.sort} order={state.order} onSort={onSort} />
                                <SortableTh label="Completed At" sortKey="completed_at" sort={state.sort} order={state.order} onSort={onSort} />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700">
                            {jobs.map((job) => (
                                <tr
                                    key={job.id}
                                    className="cursor-pointer hover:bg-body-bg/50"
                                    onClick={() => navigate(`/usage/web/jobs/${job.id}`, { state: fromHere(location) })}
                                >
                                    <td className={tdClass}>{job.project_name || '—'}</td>
                                    <td className={tdClass}>{job.operation}</td>
                                    <td className={tdClass}>{job.input_tokens_sum.toLocaleString()}</td>
                                    <td className={tdClass}>{tokensForOperation(job.operation, 'converted', job.input_tokens_converted_sum)}</td>
                                    <td className={tdClass}>{tokensForOperation(job.operation, 'supported', job.input_tokens_supported_sum)}</td>
                                    <td className={tdClass}><CliCost operation={job.operation} cost={job.cost_cents} estimate={job.estimated_cost_cents} /></td>
                                    <td className={tdClass}>{formatDateTimeUTC(job.completed_at)}</td>
                                </tr>
                            ))}
                            {jobs.length === 0 && (
                                <EmptyRow
                                    colSpan={7}
                                    text={rowsPending ? 'Loading...' : range ? 'No WEB jobs found.' : 'Pick a valid date range.'}
                                />
                            )}
                        </tbody>
                    </table>
                </TableViewport>
            </TableCard>
        </div>
    );
}

export default WebUsageTab;
